import { useEffect, useState } from "react";
import operatorApiService from "../../../services/operator-api.service";
import "../CommonDesign.scss";
import editIcon from "../../../assets/imgs/master/edit_icon_wh.svg";
import deleteIcon from "../../../assets/imgs/master/delete_icon_wh.svg";

interface EditableRole {
  role_cd: string;
  role_name: string;
  terminal_type: string;
  status: "original" | "edited" | "new" | "deleted";
  isEditing?: boolean;
  internalId: string;
  original_cd: string;
}

const Role = () => {
  const [rows, setRows] = useState<EditableRole[]>([]);
  const [originalRows, setOriginalRows] = useState<EditableRole[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchRoles = async () => {
      const res = await operatorApiService.doGetRoles();
      const initial = res.map((entry: any) => ({
        ...entry,
        original_cd: entry.role_cd,
        status: "original",
        isEditing: false,
        internalId: `existing-${entry.role_cd}`,
      }));
      setRows(initial);
      setOriginalRows(initial);
    };

    fetchRoles();
  }, []);

  const handleChange = (
    index: number,
    field: keyof EditableRole,
    value: any
  ) => {
    const updated = [...rows];
    (updated[index] as any)[field] = value;
    setRows(updated);
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        role_cd: "",
        role_name: "",
        terminal_type: "",
        status: "new",
        isEditing: true,
        internalId: `new-${Date.now()}`,
        original_cd: "",
      },
    ]);
  };

  const toggleEdit = (internalId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.internalId === internalId
          ? {
              ...r,
              isEditing: true,
              status: "edited",
              original_cd: r.original_cd ?? r.role_cd,
            }
          : r
      )
    );
  };

  const markDeleted = (internalId: string) => {
    setRows((prev) =>
      prev
        .filter((r) => {
          if (r.internalId !== internalId) return true;
          return r.status !== "new";
        })
        .map((r) =>
          r.internalId === internalId && r.status !== "new"
            ? { ...r, status: "deleted" }
            : r
        )
    );
  };

  const handleReset = () => {
    setRows(originalRows.map((r) => ({ ...r })));
  };

  const submit = async () => {
    try {
      const hasBlank = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          (r.role_cd === "" || r.role_name === "" || r.terminal_type === "")
      );

      if (hasBlank) {
        setErrorMessage("空欄があります。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const hasTooLong = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          (r.role_cd.trim().length > 2 ||
            r.role_name.trim().length > 30 ||
            r.terminal_type.trim().length > 2)
      );

      if (hasTooLong) {
        setErrorMessage("文字数が制限を超えています。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const create = rows
        .filter((r) => r.status === "new")
        .map(({ role_name, terminal_type, role_cd }) => ({
          role_cd,
          role_name,
          terminal_type,
        }));

      const update = rows
        .filter((r) => r.status === "edited")
        .map(({ role_cd, role_name, terminal_type, original_cd }) => ({
          role_cd,
          role_name,
          terminal_type,
          original_cd,
        }));

      const del = rows
        .filter((r) => r.status === "deleted")
        .map((r) => r.role_cd);

      const response = await operatorApiService.saveRoles({
        create,
        update,
        delete: del,
      });

      if (response.error === "duplicate") {
        setErrorMessage("指定された権限CDは既に使用されています。");
        setTimeout(() => setErrorMessage(null), 3000);
      }

      if (response.status === "success") {
        setSuccessMessage("更新に成功しました。");
        setTimeout(() => setSuccessMessage(null), 3000);

        const refreshed = await operatorApiService.doGetRoles();
        const mapped = refreshed.map((entry: any) => ({
          ...entry,
          status: "original",
          isEditing: false,
          internalId: `existing-${entry.role_cd}`,
        }));
        setRows(mapped);
        setOriginalRows(mapped);
      }
    } catch (error) {
      setErrorMessage("更新に失敗しました。");
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  return (
    <div className="roleClass">
      <div className="content">
        <div className="title">役割</div>
        <div className="section">
          <div className="sectionTitle">役割一覧</div>
          <div className="sectionBody">
            <table className="tableArea">
              <thead>
                <tr>
                  <th>権限CD</th>
                  <th>権限名</th>
                  <th>端末種別コード</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.internalId}>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.role_cd ?? ""}
                          onChange={(e) =>
                            handleChange(i, "role_cd", e.target.value)
                          }
                        />
                      ) : (
                        row.role_cd
                      )}
                    </td>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.role_name}
                          onChange={(e) =>
                            handleChange(i, "role_name", e.target.value)
                          }
                        />
                      ) : (
                        row.role_name
                      )}
                    </td>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.terminal_type}
                          onChange={(e) =>
                            handleChange(i, "terminal_type", e.target.value)
                          }
                        />
                      ) : (
                        row.terminal_type
                      )}
                    </td>
                    <td>
                      {row.status === "deleted" ? (
                        <span className="deletionLabel">削除予定</span>
                      ) : row.status === "edited" ? (
                        <span className="editLabel">編集予定</span>
                      ) : row.status === "new" ? (
                        <button
                          className="cancelButton"
                          onClick={() => markDeleted(row.internalId)}
                        >
                          取消
                        </button>
                      ) : (
                        <div className="buttonGroup">
                          <button
                            className="updateButton"
                            onClick={() => toggleEdit(row.internalId)}
                          >
                            <img src={editIcon} alt="" className="icon" />
                            編集
                          </button>
                          <button
                            className="deleteButton"
                            onClick={() => markDeleted(row.internalId)}
                          >
                            <img src={deleteIcon} alt="" className="icon" />
                            削除
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="createFooter">
              <div className="createLabel">
                表に新規入力欄が追加されます。
                新規役割種別を入力し、登録してください。
              </div>
              <button className="createButton" onClick={addRow}>
                新規行追加
              </button>
            </div>
            {successMessage && (
              <div className="successMessage">{successMessage}</div>
            )}
            {errorMessage && <div className="errorMessage">{errorMessage}</div>}
            {rows.some((r) => r.status !== "original") && (
              <div className="actionButtons">
                <button className="backButton" onClick={handleReset}>
                  戻る
                </button>
                <button className="decisionButton" onClick={submit}>
                  確定
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Role;
